// A store's settings (02-DESIGN/10-state/01-stores-types-and-instances.md §
// size, when full, and copies; decision 0050): its size against its fill,
// near full and full said with what happens next, what it does when full, its
// copies and its history; *Change settings* changes the size, the policy and
// the description in a form in the Settings tab's place, the service's
// refusal shown beside the field. The *Store* section of the store page's
// Settings tab.

import { useState } from "react";

import { isRefusal } from "../../platform/bus.ts";
import { Button, bytes, Choices, Field, NotAllowed, Pill, TextInput, when } from "../../ui/components.tsx";
import { Code, HistoryPolicy, MIN_BUDGET, WhenFull, type Store, type StoreUpdate } from "../../wire/state.ts";
import { FormPage } from "../../ui/form.tsx";
import { useSurface } from "../surface.tsx";
import { compacts, copiesWords, fullness, parseSize, whenFullWords } from "./settings.ts";

/** FillText is a store's fill against its size, and near full or full with what happens next. */
export function FillText({ st }: { st: Store }) {
  const f = fullness(st);
  const next =
    f === "full"
      ? compacts(st)
        ? "nothing is left to make room with; raise its size"
        : "new changes are refused; raise its size"
      : f === "near"
        ? compacts(st)
          ? "the store is making room"
          : `new changes will be refused at ${bytes(st.budget)}`
        : undefined;
  return (
    <span>
      {bytes(st.fill)} of {bytes(st.budget)}
      {next !== undefined && (
        <>
          {" "}
          <Pill tone={f === "full" ? "bad" : "info"}>{f === "full" ? "full" : "near full"}</Pill> {next}
        </>
      )}
    </span>
  );
}

/** StoreSettings is the store's settings, with *Change settings*, which opens its form in the tab's place. */
export function StoreSettings({ st, onChange }: { st: Store; onChange: () => void }) {
  return (
    <section className="section">
      <div className="section-head">
        <h2>Store</h2>
        <Button size="sm" onClick={onChange}>
          Change settings
        </Button>
      </div>
      <dl className="fields">
        <dt>Size</dt>
        <dd>
          <FillText st={st} />
        </dd>
        <dt>When full</dt>
        <dd>{whenFullWords(st)}</dd>
        <dt>Copies</dt>
        <dd>
          {copiesWords(st)}
          <div className="small faint">Chosen when the store was created; not changed here.</div>
        </dd>
        <dt>History</dt>
        <dd>
          {st.history === HistoryPolicy.full ? "Kept whole" : "Compactable"}
          <div className="small faint">Chosen when the store was created; not changed here.</div>
        </dd>
        <dt>Created</dt>
        <dd>{when(st.created)}</dd>
      </dl>
    </section>
  );
}

/** ChangeSettings is *Change settings*: size, when full and description, in the Settings tab's place. */
export function ChangeSettings({
  st,
  onClose,
  onChanged,
}: {
  st: Store;
  onClose: () => void;
  onChanged: (st: Store) => void;
}) {
  const s = useSurface();
  const [size, setSize] = useState(bytes(st.budget));
  const [whenFull, setWhenFull] = useState<WhenFull>(compacts(st) ? WhenFull.compact : WhenFull.refuse);
  const [description, setDescription] = useState(st.description ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const full = st.history === HistoryPolicy.full;
  const sizeN = parseSize(size);
  const sizeOK = sizeN !== undefined && sizeN >= MIN_BUDGET;
  const sizeRefused = isSizeRefusal(err);
  const submit = () => {
    if (busy || !sizeOK) {
      return;
    }
    const req: StoreUpdate = {};
    if (sizeN !== st.budget) {
      req.budget = sizeN;
    }
    if (whenFull !== (compacts(st) ? WhenFull.compact : WhenFull.refuse)) {
      req.when_full = whenFull;
    }
    if (description.trim() !== (st.description ?? "")) {
      req.description = description.trim();
    }
    if (Object.keys(req).length === 0) {
      onClose();
      return;
    }
    setErr(undefined);
    setBusy(true);
    s.bus
      .stores()
      .updateStore(st.name, req)
      .then(
        (next) => {
          setBusy(false);
          onChanged(next);
        },
        (e: unknown) => {
          setBusy(false);
          setErr(e);
        },
      );
  };
  return (
    <FormPage
      title={`Change ${st.name}'s settings`}
      heading="h2"
      back="Settings"
      onClose={onClose}
      foot={
        <>
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" onClick={submit} disabled={busy || !sizeOK}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field
          label="Size"
          hint={
            sizeRefused ? (
              <NotAllowed err={err} what="Size" />
            ) : sizeOK ? (
              `How much history ${st.name} may hold. It holds ${bytes(st.fill)} now and cannot shrink below that.`
            ) : (
              `A size such as 256 MiB or 1 GiB; at least ${bytes(MIN_BUDGET)}.`
            )
          }
        >
          <TextInput value={size} onChange={setSize} placeholder="256 MiB" />
        </Field>
        <div className="field">
          <span className="label">When full</span>
          <WhenFullChoices value={whenFull} onChange={setWhenFull} full={full} />
        </div>
        <Field label="Description" hint="For people.">
          <TextInput value={description} onChange={setDescription} />
        </Field>
        {err !== undefined && !sizeRefused && <NotAllowed err={err} what="Save" />}
      </form>
    </FormPage>
  );
}

/** isSizeRefusal reports whether the service refused the size: no room for it, below the fill, outside the install's bounds. */
function isSizeRefusal(err: unknown): boolean {
  if (!isRefusal(err)) {
    return false;
  }
  return (
    err.code === Code.budget ||
    ((err.code === Code.invalid || err.code === Code.badRequest) && err.message.includes("size"))
  );
}

/** WhenFullChoices is the choice of what a store does at its size; a store kept whole can only refuse. */
export function WhenFullChoices({
  value,
  onChange,
  full,
}: {
  value: WhenFull;
  onChange: (v: WhenFull) => void;
  full: boolean;
}) {
  return (
    <Choices
      name="when-full"
      value={value}
      onChange={onChange}
      options={[
        {
          value: WhenFull.refuse,
          label: "Refuse new changes",
          hint: "At its size the store takes nothing more, and nothing in it moves. The usual choice.",
        },
        {
          value: WhenFull.compact,
          label: "Make room by keeping only current state",
          hint: full
            ? "Not for a store whose history is kept whole: it would give up the changes it keeps."
            : "From nine tenths of its size, each item's older changes are replaced by its current state. Every item stays.",
          disabled: full,
        },
      ]}
    />
  );
}
